<template>
  <div class="body-1 article-content" v-html="content" :class="categorySlug"></div>
</template>

<script>
  export default {
    name: 'ArticleContent',
    props: ['content', 'categorySlug']
  }
</script>

<style lang="scss">
  .article-content {
    section {
      @apply bg-white;
      padding-bottom: 90px;

      &:nth-child(2n) {
        background-color: #f5f5f5;
      }
    }

    figure {
      display: table;
      @apply my-9;

      img {
        width: 100%;
      }
    }

    figcaption {
      font-family: "Source Sans Pro", sans-serif;
      font-weight: 600;
      font-size: 18px;
      color: #ffffff;
      width: 100%;
      padding: 30px;
      border-radius: 0 0 50px 50px;
      background-color: lightslategrey;
      display: table-caption;
      caption-side: bottom;

      p {
        color: #fff;
      }
    }

    figcaption.image-intro {
      font-family: "Source Sans Pro", sans-serif;
      font-weight: bold;
      font-size: 22px;
      color: white;
      background-color: lightslategrey;
    }

    table {
      th {
        @apply font-bold text-left;
      }

      thead {
        th, td {
          @apply font-bold text-left;
        }
      }

      td, th {
        @apply pr-3;
      }
    }

    h4 {
      font-size: 30px;
      @apply uppercase;
    }

    h6 {
      @apply font-heading-primary uppercase;
      font-size: 24px;
    }

    .section-header {
      &.primary {
        @apply uppercase text-white leading-none flex items-center mb-9;
        background-color: lightslategrey;
        padding: 15px 0 5px 95px;
        font-size: 52px;
      }

      &.secondary {
        height: 60px;
        align-items: center;
        margin: 40px auto;
        justify-content: center;
        font-size: 30px;
        @apply text-white uppercase flex;
        background: no-repeat url("../../../assets/images/93-lrn-spt-prof-div-2-cat-1.svg") center;

        @media (min-width: config('screens.lg')) {
          width: 920px;
        }
      }
    }

    .section-intro {
      @apply font-heading-secondary font-bold uppercase;
      font-size: 24px;

      .glossary-term {
        @apply uppercase font-heading-secondary;
        font-size: 24px;
      }
    }

    .glossary-term {
      @apply text-black;
      outline: none;
    }

    .column-row {
      @apply flex mt-9;

      > div {
        @apply px-3;
        flex-basis: 100%;

        &:only-child {
          @apply w-full;

          @media (min-width: config('screens.xl')) {
            margin: 0 110px;
          }
        }

        &:first-child:not(:last-child) {
          flex: 1 0 50%;

          @media (min-width: config('screens.xl')) {
            margin-left: 110px;
            flex: 1 0 550px;
          }
        }

        & > :first-child {
          @apply mt-0;
        }
      }
    }

    .sidebar-rule {
      padding-top: 38px;

      p {
        @apply uppercase;
        font-size: 24px;
      }

      .glossary-term {
        font-size: inherit;
        font-family: inherit;
      }

      &::before {
        content: '';
        height: 8px;
        width: 160px;
        position: absolute;
        margin-top: -38px;
        background-color: lightslategrey;
        border-radius: 25px;
      }
    }

    .sidebar-quote {
      padding-top: 79px;

      p {
        font-size: 24px;
        text-transform: uppercase;
      }

      &::before {
        content: '';
        width: 65px;
        height: 49px;
        position: absolute;
        margin-top: -79px;
        background: no-repeat url("../../../assets/images/88-lrn-spt-prof-side-quote-cat-1.svg") center;
      }
    }

    cite.sidebar-1 {
      font-size: 18px;
      color: #808080;
    }

    .sidebar-boxed {
      @apply py-6 px-3 my-9;
      background-color: lightslategrey;
      border-radius: 40px 0 0 40px;

      p {
        @apply text-white;

        &:last-child {
          @apply mb-0;
        }
      }
    }

    p {
      @apply mb-4;

      .glossary-term {
        @apply font-primary font-bold relative;
        font-size: 20px;
        cursor: default;

        &:before,
        &:after {
          content: ' ';
          @apply font-primary font-normal text-white bg-lerner-blue absolute border-lerner-blue normal-case;
          font-size: 20px;
          opacity: 0;
        }

        &:hover:before, &:hover:after,
        &:focus:before, &:focus:after {
          @apply border-lerner-blue;
          opacity: 1;
          transition: opacity 0.2s ease-in-out;
        }

        &:hover:before,
        &:focus:before {
          content: ' ';
          @apply text-lerner-blue bg-transparent;
          border-left: 10px solid transparent;
          border-right: 10px solid transparent;
          border-top: 10px solid;
          width: 0;
          height: 0;
          transform: translateX(-50%);
          left: 50%;
        }

        &:hover:after,
        &:focus:after {
          content: attr(data-title);
          padding: 2px 5px;
          border-radius: 4px;
          transform: translateX(-50%);
          bottom: 100%;
          min-width: 500px;
          text-align: center;
        }
      }
    }

    &.basketball {
      h4,
      h6,
      p.section-intro {
        @apply text-c1-g;
      }

      .section-header.primary {
        @apply bg-c1-b;
      }

      .sidebar-1 p {
        @apply text-c1-g;
      }

      .sidebar-quote {
        &::before {
          background: no-repeat url("../../../assets/images/88-lrn-spt-prof-side-quote-cat-1.svg") center;
        }
      }

      .sidebar-rule {
        &::before {
          @apply bg-c1-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c1-e;
      }

      .section-secondary {
        background: no-repeat url("../../../assets/images/93-lrn-spt-prof-div-2-cat-1.svg") center;
      }

      figcaption {
        @apply bg-c1-e;
      }
    }

    &.football {
      h4,
      h6,
      p.section-intro {
        @apply text-c2-g;
      }

      .section-header.primary {
        @apply bg-c2-b;
      }

      .sidebar-1 p {
        @apply text-c2-g;
      }

      .sidebar-quote {
        &::before {
          background: no-repeat url("../../../assets/images/89-lrn-spt-prof-side-quote-cat-2.svg") center;
        }
      }

      .sidebar-rule {
        &::before {
          @apply bg-c2-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c2-e;
      }

      .section-secondary {
        background: no-repeat url("../../../assets/images/94-lrn-spt-prof-div-2-cat-2.svg") center;
      }

      figcaption {
        @apply bg-c2-e;
      }
    }

    &.baseball {
      h4,
      h6,
      p.section-intro {
        @apply text-c3-g;
      }

      .section-header.primary {
        @apply bg-c3-b;
      }

      .sidebar-1 p {
        @apply text-c3-g;
      }

      .sidebar-quote {
        &::before {
          background: no-repeat url("../../../assets/images/90-lrn-spt-prof-side-quote-cat-3.svg") center;
        }
      }

      .sidebar-rule {
        &::before {
          @apply bg-c3-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c3-e;
      }

      .section-secondary {
        background: no-repeat url("../../../assets/images/95-lrn-spt-prof-div-2-cat-3.svg") center;
      }

      figcaption {
        @apply bg-c3-e;
      }
    }

    &.soccer {
      h4,
      h6,
      p.section-intro {
        @apply text-c4-g;
      }

      .section-header.primary {
        @apply bg-c4-b;
      }

      .sidebar-1 p {
        @apply text-c4-g;
      }

      .sidebar-quote {
        &::before {
          background: no-repeat url("../../../assets/images/91-lrn-spt-prof-side-quote-cat-4.svg") center;
        }
      }

      .sidebar-rule {
        &::before {
          @apply bg-c4-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c4-e;
      }

      .section-secondary {
        background: no-repeat url("../../../assets/images/96-lrn-spt-prof-div-2-cat-4.svg") center;
      }

      figcaption {
        @apply bg-c4-e;
      }
    }

    &.more-sports {
      h4,
      h6,
      p.section-intro {
        @apply text-c5-g;
      }

      .section-header.primary {
        @apply bg-c5-b;
      }

      .sidebar-1 p {
        @apply text-c5-g;
      }

      .sidebar-quote {

        &::before {
          background: no-repeat url("../../../assets/images/92-lrn-spt-prof-side-quote-cat-5.svg") center;
        }
      }

      .sidebar-rule {
        &::before {
          @apply bg-c5-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c5-e;
      }

      .section-secondary {
        background: no-repeat url("../../../assets/images/97-lrn-spt-prof-div-2-cat-5.svg") center;
      }

      figcaption {
        @apply bg-c5-e;
      }
    }

    &.olympics {
      h4,
      h6,
      p.section-intro {
        @apply text-c6-g;
      }

      .section-header.primary {
        @apply bg-c6-b;
      }

      .sidebar-1 p {
        @apply text-c6-g;
      }

      .sidebar-quote {

        &::before {
          background: no-repeat url("../../../assets/images/olympics-category-quote-icon.svg") center;
        }
      }

      .sidebar-rule {
        &::before {
          @apply bg-c6-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c6-e;
      }

      .section-secondary {
        background: no-repeat url("../../../assets/images/olympics-category-all-star-stats.svg") center;
      }

      figcaption {
        @apply bg-c6-e;
      }
    }

    &.hockey {
      h4,
      h6,
      p.section-intro {
        @apply text-c7-g;
      }

      .section-header.primary {
        @apply bg-c7-b;
      }

      .sidebar-1 p {
        @apply text-c7-g;
      }

      .sidebar-quote {

        &::before {
          background: no-repeat url("../../../assets/images/hockey-category-quote-icon.svg") center;
        }
      }

      .sidebar-rule {
        &::before {
          @apply bg-c7-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c7-e;
      }

      .section-secondary {
        background: no-repeat url("../../../assets/images/hockey-category-all-star-stats.svg") center;
      }

      figcaption {
        @apply bg-c7-e;
      }
    }

    &.tennis {
      h4,
      h6,
      p.section-intro {
        @apply text-c8-g;
      }

      .section-header.primary {
        @apply bg-c8-b;
      }

      .sidebar-1 p {
        @apply text-c8-g;
      }

      .sidebar-quote {

        &::before {
          background: no-repeat url("../../../assets/images/tennis-category-quote-icon.svg") center;
        }
      }

      .sidebar-rule {
        &::before {
          @apply bg-c8-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c8-e;
      }

      .section-secondary {
        background: no-repeat url("../../../assets/images/tennis-category-all-star-stats.svg") center;
      }

      figcaption {
        @apply bg-c8-e;
      }
    }

    &.default {
      p.section-intro {
        @apply text-grey-1;
      }

      .section-header.primary {
        @apply bg-grey-1;
      }

      .sidebar-1 p {
        @apply text-grey-1;
      }

      .sidebar-quote {

        &::before {
          fill: config('colors.grey-1');
        }
      }

      .sidebar-rule {
        &::before {
          @apply bg-grey-1;
        }
      }

      .sidebar-boxed {
        @apply bg-grey-1;
      }

      .section-secondary {
        background: no-repeat url("../../../assets/images/93-lrn-spt-prof-div-2-cat-1.svg") center;
      }

      figcaption {
        @apply bg-grey-1;
      }
    }
  }
</style>
